/**
 * Daily Report — one source for both views, as in the original app:
 * `reportData()` collects the day, `buildReport()` writes the copyable plain text,
 * and the formatted view (features/report/ReportViews.tsx) renders the same data.
 * The text format must not change: people paste it into emails and chats.
 */
import type { Backup, MetricKind, Project, ProjectMetric } from '@/types/models';
import { longDate, sum } from './format';
import { kindOf } from './metrics';

export type ReportSource = Pick<Backup, 'projects' | 'members' | 'logs' | 'updates' | 'settings'>;

export interface ReportWorkGroup {
  memberIds: string[];
  names: string[];
  hours: number;
  work: string[];
}
export interface ReportProject {
  project: Project;
  work: ReportWorkGroup[];
  note: string;
  metrics: (ProjectMetric & { kind: MetricKind | '' })[];
  hours: number;
  people: number;
}
export interface DailyReport {
  date: string;
  title: string;
  /** "Management" line: configured management members with more than 0 hours that day */
  general: { memberId: string; name: string; hours: number }[];
  projects: ReportProject[];
}

export const reportHrs = (h: number): string => (h < 1 ? `${Math.round(h * 60)} minutes` : `${h} hour${h === 1 ? '' : 's'}`);
export const reportNames = (names: string[]): string =>
  names.length > 1 ? names.slice(0, -1).join(', ') + ' and ' + names.slice(-1) : names[0];

export function reportData(src: ReportSource, date: string): DailyReport {
  const memberName = (id: string) => src.members.find((m) => m.id === id)?.name || '—';
  const logs = src.logs.filter((l) => l.date === date);
  const ups = src.updates.filter((u) => u.date === date);
  const gen = src.settings.generalMembers || [];
  const title = (src.settings.reportTitle || 'Daily Update - {date}').replace('{date}', longDate(date));
  const general = gen
    .map((mid) => ({ memberId: mid, name: memberName(mid), hours: sum(logs.filter((l) => l.memberId === mid).map((l) => l.hours)) }))
    .filter((x) => x.hours > 0);
  const projects: ReportProject[] = [];
  for (const p of src.projects) {
    const pl = logs.filter((l) => l.projectId === p.id);
    const u = ups.find((x) => x.projectId === p.id);
    if (!pl.length && !u) continue;
    // Same grouping as the original: people with the same work and hours are listed together.
    const byM: Record<string, { h: number; w: string[] }> = {};
    pl.forEach((l) => {
      if (!byM[l.memberId]) byM[l.memberId] = { h: 0, w: [] };
      byM[l.memberId].h += Number(l.hours);
      if (l.work) byM[l.memberId].w.push(l.work);
    });
    const sameWork: Record<string, string[]> = {};
    Object.entries(byM).forEach(([mid, x]) => {
      const k = x.w.join('; ') + '|' + x.h;
      (sameWork[k] = sameWork[k] || []).push(mid);
    });
    const work = Object.values(sameWork).map((mids) => ({ memberIds: mids, names: mids.map(memberName), hours: byM[mids[0]].h, work: byM[mids[0]].w }));
    const metrics = u && u.metrics.length ? u.metrics.map((m) => ({ ...m, kind: kindOf(p, m.label) })) : [];
    projects.push({ project: p, work, note: u && u.note ? u.note : '', metrics, hours: sum(pl.map((l) => l.hours)), people: new Set(pl.map((l) => l.memberId)).size });
  }
  return { date, title, general, projects };
}

/** The copyable plain text (byte-for-byte the original app's format). */
export function buildReport(r: DailyReport): string {
  const lines: string[] = [r.title, ''];
  if (r.general.length) {
    lines.push('Today, ' + r.general.map((x) => `${x.name} worked on the projects for around ${reportHrs(x.hours)}`).join(', and ') + '.');
    lines.push('');
  }
  r.projects.forEach((x) => {
    lines.push(x.project.name);
    x.work.forEach((g) => lines.push(`${reportNames(g.names)} worked for ${reportHrs(g.hours)}${g.work.length ? ' on ' + g.work.join('; ') : ''}.`));
    if (x.note) lines.push(x.note);
    x.metrics.forEach((m) => lines.push(`${m.label}${m.kind === 'daily' ? ' (today)' : m.kind === 'todate' ? ' (to date)' : ''}: ${fmtNumCompat(m.value)}`));
    lines.push('');
  });
  return lines.join('\n').trim();
}

// Kept local so the report text never depends on a change elsewhere.
function fmtNumCompat(v: unknown): string {
  const n = Number(String(v).replace(/,/g, ''));
  return isNaN(n) || v === '' ? String(v) : n.toLocaleString('en-IN');
}

/** All dates that have a work log or project log, newest first (report date picker). */
export const reportDates = (src: Pick<Backup, 'logs' | 'updates'>): string[] =>
  [...new Set([...src.logs.map((l) => l.date), ...src.updates.map((u) => u.date)])].sort().reverse();
